import { TextAreaField } from '@aws-amplify/ui-react'
import { DialogContent } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDialog from '@/components/feedback/CustomDialog'
import { ConfigReviewDialogActions } from '@/features/controller/configuration/components/ConfigReviewDialogActions'
import { DiffViewer } from '@/features/controller/configuration/components/textEditor/DiffViewer'
import type { ControllerConfigVersionUpdate } from '@/features/controller/configuration/types'
import { TextEditorLanguage } from '@/features/controller/configuration/types'

interface ConfigReviewEditsDialogProps {
  isUpdating?: boolean
  language?: TextEditorLanguage
  modifiedText: string
  onClose: () => void
  onSave: (update: ControllerConfigVersionUpdate, formattedText: string) => void
  onSaveAndRestart: (update: ControllerConfigVersionUpdate, formattedText: string) => void
  open: boolean
  originalText: string
  originalVersionId: string
  proposedComment?: string
}

/**
 * Format JSON text with proper indentation and spacing before submission to server.
 */
function formatJsonText(text: string): string {
  try {
    const parsed = JSON.parse(text)
    return JSON.stringify(parsed, null, 2)
  } catch {
    // Invalid JSON should not pass here, but in that case return original text
    return text
  }
}

export function ConfigReviewEditsDialog({
  open,
  originalVersionId,
  onSave,
  onSaveAndRestart,
  onClose,
  originalText,
  modifiedText,
  proposedComment,
  isUpdating = false,
  language = TextEditorLanguage.ini,
}: Readonly<ConfigReviewEditsDialogProps>) {
  const [comment, setComment] = useState('')

  const { t } = useTranslation()

  useEffect(() => {
    if (comment === '') {
      setComment(proposedComment || '')
    }
  }, [proposedComment])

  const isJson = language === TextEditorLanguage.json

  const formattedModifiedText = isJson ? formatJsonText(modifiedText) : modifiedText

  const handleSave = () => {
    onSave(
      {
        previousVersionId: originalVersionId,
        value: formattedModifiedText,
        comment: comment.trim() || undefined,
      },
      formattedModifiedText,
    )
    setComment('')
  }

  const handleSaveAndRestart = () => {
    onSaveAndRestart(
      {
        previousVersionId: originalVersionId,
        value: formattedModifiedText,
        comment: comment.trim() || undefined,
      },
      formattedModifiedText,
    )
    setComment('')
  }

  // Note: this is a placeholder dialog that will be fleshed out in the next phase of the development
  return (
    <CustomDialog
      open={open}
      size="extraLarge"
      title={t('controllers.configuration.review_dialog.title')}
      onClose={onClose}
    >
      <DialogContent>
        <DiffViewer language={language} modifiedText={formattedModifiedText} originalText={originalText} />
        <TextAreaField
          label={t('controllers.configuration.comment')}
          placeholder={t('controllers.configuration.comment_placeholder')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </DialogContent>
      <ConfigReviewDialogActions
        isLoading={isUpdating}
        onCancel={onClose}
        onSave={handleSave}
        onSaveAndRestart={handleSaveAndRestart}
      />
    </CustomDialog>
  )
}
